import { Color, ScreenSpaceEventType, } from "cesium";
import { CesiumPointSelectorType, } from "../../cesium-utils/point-selector/point-selector";
import { EarthBrainUtils } from "../../utilities/earthbrainutility";
/**
 * 仮設道路AI の中継点ピック用の軽量ハンドラ。
 * 道路オブジェクトは生成せず、点群優先で拾った順序付き点列（始点・中継点・終点）のみを返す。
 */
export class RoadAiPointPickHandler {
    _viewer;
    _preview;
    _handler;
    _selector;
    _points = [];
    _redo = [];
    _spheres = [];
    _hoverPos;
    _active = false;
    _maxPoints = 7;
    _onFinish;
    _onLimitExceeded;
    _keydownHandler;
    constructor(args) {
        this._viewer = args.viewer;
        this._preview = args.preview;
        this._handler = args.handler;
        // 設計面ではなく点群（地盤）上を拾うため点群優先セレクタを使う。
        this._selector = args.cesiumPointSelectorFactory.getPointSelector(CesiumPointSelectorType.PriorityBased);
    }
    isActive() {
        return this._active;
    }
    /**
     * ピックを開始する。onFinish には順序付きの点列（WGS84）が渡る。
     * onLimitExceeded is called on every click once maxPoints is reached (no point is added).
     */
    start(onFinish, maxPoints = 7, onLimitExceeded) {
        this._onFinish = onFinish;
        this._onLimitExceeded = onLimitExceeded;
        this._maxPoints = maxPoints;
        this._points = [];
        this._redo = [];
        this._spheres = [];
        this._hoverPos = undefined;
        this._active = true;
        this._preview.setSphereVisibility(true);
        this._handler.setInputAction((m) => this._onMove(m.endPosition), ScreenSpaceEventType.MOUSE_MOVE);
        this._handler.setInputAction((m) => this._onClick(m.position), ScreenSpaceEventType.LEFT_CLICK);
        this._keydownHandler = (e) => {
            if (e.key === "Enter")
                this.finish();
            else if (e.key === "Escape" || e.key === "Esc")
                this.cancel();
        };
        document.addEventListener("keydown", this._keydownHandler);
    }
    async _onMove(mousePos) {
        const p = await this._selector.getPointOnMousePos(mousePos);
        if (!p)
            return;
        this._hoverPos = p;
        this._preview.setSpherePosition(p);
        this._updateLine();
        this._viewer.scene.requestRender();
    }
    async _onClick(mousePos) {
        if (this._points.length >= this._maxPoints) {
            this._onLimitExceeded?.();
            return;
        }
        const p = await this._selector.getPointOnMousePos(mousePos);
        if (!p)
            return;
        // Re-check: other clicks may have filled the limit while awaiting the pick.
        if (this._points.length >= this._maxPoints) {
            this._onLimitExceeded?.();
            return;
        }
        this._points.push(p);
        this._redo = [];
        this._spheres.push(EarthBrainUtils.addSphere(this._viewer, p, Color.CRIMSON));
        this._updateLine();
        this._viewer.scene.requestRender();
    }
    /** 直前の点を取り消す（戻る）。 */
    undo() {
        if (!this._active || this._points.length === 0)
            return;
        const p = this._points.pop();
        if (p)
            this._redo.push(p);
        const s = this._spheres.pop();
        if (s)
            this._viewer.entities.remove(s);
        this._updateLine();
        this._viewer.scene.requestRender();
    }
    /** 取り消した点を復元する（進む）。 */
    redo() {
        if (!this._active || this._redo.length === 0)
            return;
        if (this._points.length >= this._maxPoints)
            return;
        const p = this._redo.pop();
        if (!p)
            return;
        this._points.push(p);
        this._spheres.push(EarthBrainUtils.addSphere(this._viewer, p, Color.CRIMSON));
        this._updateLine();
        this._viewer.scene.requestRender();
    }
    /** 完了。最小 2 点（始点＋終点）に満たない場合は何もしない。 */
    finish() {
        if (!this._active || this._points.length < 2)
            return;
        const points = this._points.slice();
        const cb = this._onFinish;
        this._cleanup();
        cb?.(points);
    }
    /** 中断。空配列でコールバックを解決する。 */
    cancel() {
        if (!this._active)
            return;
        const cb = this._onFinish;
        this._cleanup();
        cb?.([]);
    }
    _updateLine() {
        const pts = [...this._points];
        if (this._hoverPos)
            pts.push(this._hoverPos);
        if (pts.length >= 2) {
            this._preview.setLineVisibility(true);
            this._preview.setLinePosition(pts);
        }
        else {
            this._preview.setLinePosition([]);
        }
    }
    _cleanup() {
        this._active = false;
        this._onFinish = undefined;
        this._onLimitExceeded = undefined;
        this._handler.removeInputAction(ScreenSpaceEventType.MOUSE_MOVE);
        this._handler.removeInputAction(ScreenSpaceEventType.LEFT_CLICK);
        this._preview.setSphereVisibility(false);
        this._preview.setLineVisibility(false);
        this._preview.setLinePosition([]);
        for (const s of this._spheres)
            this._viewer.entities.remove(s);
        this._spheres = [];
        this._points = [];
        this._redo = [];
        this._hoverPos = undefined;
        if (this._keydownHandler) {
            document.removeEventListener("keydown", this._keydownHandler);
            this._keydownHandler = undefined;
        }
        this._viewer.scene.requestRender();
    }
}
//# sourceMappingURL=road-ai-point-pick-handler.js.map